import {Box, Stack, Typography} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import ArrButton from '../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {
  useConfigKeyValue,
  useGoToDocList,
} from '../../../hooks/categorization/useCategorization';
import ArrModal from '../../common/arr-modal/ArrModal';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  CategorizationGoToDocListParams,
  CategorizationViewResultsProps,
} from '../../../api/types/categorization.type';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {BgNavigationTypes, SourceTypeCode} from '../../../shared/constants';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../redux/slice/docListSlice';
import {useNavigate} from 'react-router';
import ViewResultsModal from './ViewResultsModal';

interface ICategorizationResultProps {
  data: CategorizationViewResultsProps;
}

const CategorizationResult = ({data}: ICategorizationResultProps) => {
  const [openModal, setOpenModal] = useState(false);
  const [goToDocListPayload, setGoToListPayload] =
    useState<CategorizationGoToDocListParams>({
      ProviewId: null,
      ThresholdFlag: false,
      TotalDocumentToView: null,
    });

  const [continueModal, setContinueModal] = useState<boolean>(false);
  const currentElement = useRef<HTMLDivElement | null>(null);
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    data: configValue,
    isLoading: configLoading,
    refetch: fetchConfig,
  } = useConfigKeyValue('DOCLIST');
  const {data: goToDocListData, isLoading: goToDocListLoading} =
    useGoToDocList(goToDocListPayload);

  const getDocList = async () => {
    const result = await fetchConfig();
    if (result.status === 'success') handleApply(result.data);
  };

  useEffect(() => {
    if (!continueModal) return;
    if (lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_COMPLETE) {
      const {navigationDestinationType: navType, notificationId} =
        lastEvent?.payload?.payload || {};
      if (
        notificationId === Number(goToDocListData?.masterDocId) &&
        navType === SourceTypeCode.DOCLIST
      ) {
        const backPath = `${window.location.pathname}${window.location.search}`;
        if (!backPath.includes('proviewId')) return; // to avoid setting back navigation on proview navigation
        dispatch(setDocListId(Number(goToDocListData?.masterDocId)));
        dispatch(
          setDocListBackNavigation({
            label: t('categorization.title'),
            path: backPath,
          })
        );
        navigate('/Document/DocList');
        setContinueModal(false);
      }
    }
  }, [lastEvent]);

  useEffect(() => {
    if (openModal) {
      currentElement.current = document.activeElement as HTMLDivElement;
    } else {
      if (currentElement.current) {
        (currentElement.current as HTMLElement).focus();
        currentElement.current = null;
      }
    }
  }, [openModal]);

  const handleCancel = () => {
    setOpenModal(false);
  };
  const handleApply = (result = configValue) => {
    if (result < data?.documentCount && !openModal) {
      setOpenModal(true);
      return;
    }
    setGoToListPayload({
      ProviewId: data.proviewId || 0,
      ThresholdFlag: true,
      TotalDocumentToView: result || 0,
    });
    setOpenModal(false);
    setContinueModal(true);
  };

  return (
    <>
      <ViewResultsModal
        continueModal={continueModal}
        setContinueModal={setContinueModal}
        masterDocId={goToDocListData?.masterDocId}
      />
      <ArrLoadingSpinner
        overlay={true}
        isOpen={configLoading || goToDocListLoading}
        color="secondary"
      />
      <ArrModal
        showModal={openModal}
        onClose={handleCancel}
        dataTestId="categarization-result-modal"
        buttons={[
          {
            id: '1',
            label: t('common.cancel'),
            onClick: handleCancel,
            variant: 'secondary',
            dataTestId: 'crm-modal-cancel-btn',
          },
          {
            id: '2',
            label: t('common.continue'),
            onClick: () => handleApply(),
            variant: 'primary',
            dataTestId: 'crm-modal-confirm-btn',
          },
        ]}
        autoFocusCloseButton={true}
      >
        <Typography
          className="typo-text body2_size text-body-strong"
          tabIndex={0}
          data-testid={`categorization-result-description`}
        >
          {t('categorization.docListModal.description', {
            configLimit: (configValue / 1000).toFixed(1),
          })}
        </Typography>
      </ArrModal>
      <Box className="categorization-result">
        <Box className="documents-cohesion">
          <Stack className="visualization">
            <Typography
              className="typo-text display3_size documents"
              tabIndex={0}
              data-testid="document-count"
            >
              {data?.documentCount || 0}
            </Typography>
            <Typography
              className="typo-text label2_size text-body-strong"
              tabIndex={0}
              data-testid={`categorization-result-documents`}
            >
              {t('categorization.actualResults.documents')}
            </Typography>
          </Stack>
          <Stack className="visualization">
            <Typography
              className={`typo-text display3_size ${data?.cohesionLevel >= 0.3 ? 'cohesion' : 'low-cohesion'}`}
              tabIndex={0}
              data-testid={`categorization-result-cohesion-level`}
            >
              {(data?.cohesionLevel * 100).toFixed(0)}%
            </Typography>
            <Typography
              className="typo-text label2_size text-body-strong"
              tabIndex={0}
              data-testid={`categorization-result-cohesion`}
            >
              {t('categorization.actualResults.cohesion')}
            </Typography>
          </Stack>
        </Box>
        <Box>
          <Typography
            className="typo-text body3_size text-headings"
            tabIndex={0}
            data-testid={`categorization-result-paragraph1-paragraph2`}
          >
            <p data-testid={`categorization-result-paragraph1`}>
              {t('categorization.actualResults.paragraph1')}
            </p>
            <p data-testid={`categorization-result-paragraph2`}>
              {t('categorization.actualResults.paragraph2')}
            </p>
          </Typography>
        </Box>
        <Box className="view-in-doc-list">
          <Typography
            className={'typo-text label1_size text-headings'}
            tabIndex={0}
            data-testid={`categorization-result-analyze-further`}
          >
            {t('categorization.actualResults.analyzeFurther')}
          </Typography>
          <ArrButton
            variant="primary"
            size="large"
            onClick={getDocList}
            dataTestId="bulk-release-cancel-button"
            aria-label={t('categorization.actualResults.viewResultsInDocList')}
          >
            {t('categorization.actualResults.viewResultsInDocList')}
          </ArrButton>
        </Box>
      </Box>
    </>
  );
};

export default CategorizationResult;
